import { useEffect, useState } from 'react';
import { useFirmScopedPermissions } from 'common/hooks';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import { FormDialog } from 'components';
import { CapTableForm } from 'components/Forms';
import { CapTableDefaultFormState, CapTableFormState } from 'components/Forms/CapTableForm/CapTableForm.props';
import { useCapTableAccessLevelPermissions } from 'pages/CapTable/cap-table/components/captable-table/hooks';
import { useUpdateCaptableInfo } from 'services/hooks/captable';
import { CapTableDialogProps } from './CapTableDialog.props';

const CapTableDialog = ({ open, onClose, onSave }: CapTableDialogProps) => {
  const [, updateCapTableData] = useUpdateCaptableInfo({ captableId: null, captableInfo: null });
  const [{ captableInfo, user }] = useStore() as unknown as UseStoreValues;
  const { currentFirmRole } = useFirmScopedPermissions();

  const currentUserId = user?.id ?? null;

  const { canEditAccessLevel } = useCapTableAccessLevelPermissions({
    captableInfo,
    currentFirmRole,
    currentUserId,
  });

  const [formState, setFormState] = useState<CapTableFormState>({
    dbErrors: {},
    errors: {},
    isValid: false,
    touched: {},
    values: {},
  });

  useEffect(() => {
    if (captableInfo) {
      setFormState(prevFormState => ({
        ...prevFormState,
        values: {
          name: captableInfo.name,
          ...(!captableInfo.is_primary && { access_level: captableInfo.access_level }),
        },
      }));
    }
  }, [captableInfo]);

  if (!captableInfo) {
    return null;
  }

  const saveData = async () => {
    // sheet_attribute_mapping is an integer ID in the store (read shape) but the
    // write serializer expects a dict or null. Exclude it so the backend leaves
    // the existing mapping untouched.
    const captableInfoWithoutMapping = { ...captableInfo };
    delete captableInfoWithoutMapping.sheet_attribute_mapping;
    const temporalCaptableInfo = {
      ...captableInfoWithoutMapping,
      name: formState.values.name,
      ...(canEditAccessLevel && { access_level: formState.values.access_level }),
    };
    await updateCapTableData(captableInfo.id, temporalCaptableInfo);
    onSave();
  };

  return (
    <FormDialog
      open={open}
      title={captableInfo.name}
      onSave={saveData}
      onClose={onClose}
      isValid={formState.isValid}
      editMode>
      <CapTableForm
        defaultFormState={formState as CapTableDefaultFormState}
        onFormChange={prevFormState => setFormState(prevFormState)}
        accessLevelDisabled={!canEditAccessLevel}
      />
    </FormDialog>
  );
};

export default CapTableDialog;
